import {Component, EventEmitter, Input, Output, OnInit, OnDestroy} from '@angular/core';
import {Subject} from 'rxjs/Subject';
import {Search} from '../../shared/models/search/search.model';
import {Sondage} from '../../shared/models/sondage/sondage.model';
import {SondageType} from '../../shared/models/sondage/sondage.interface';
import * as pluginDataLabels from 'chartjs-plugin-datalabels';
import {NavController, NavParams} from 'ionic-angular';
import {SondageReponseService, SondageService, UserStorageService} from '../../shared/services';
import {OauthService} from '../../shared/services/oauth.service';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MessageService} from '../../shared/services/message.service';
import {ImagesService} from '../../shared/services/images.service';
import {SondageReponse} from '../../shared/models/sondage/sondageReponse.model';
import {LoadingService} from '../../shared/services/loading.service';
import {ToastDirective} from '../../shared/directives/toast/toast';
import {ResponseMessage} from '../../shared/models/responseMessage/responseMessage.model';
import {SondageValeur} from '../../shared/models/sondage/sondageValeur.model';
import Chart from 'chart.js';

@Component({
  selector: 'core-sondage-list',
  templateUrl: './sondage-list.component.html'
})
export class SondageListComponent implements OnInit, OnDestroy {

  @Input() searchQuery$: Subject<Search> = null;

  @Output() loaded: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() voted: EventEmitter<boolean> = new EventEmitter<boolean>();

  public isElu = false;

  public SondageType = SondageType;
  public offset: number = 0;
  public limit: number = 15;
  public sondages: Array<Sondage>;
  public sondageFormGroups: FormGroup[] = [];

  public noResult = true;
  public totalPages = false;
  public isLoading = new Subject();
  public formErrors: any = {};

  public isVoting = false;

  public showResult: {} = {};
  public barChartConfig: { [key: string]: any } = {
    chartType: 'horizontalBar',
    options: {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        position: 'left'
      },
      plugins: {
        datalabels: {
          anchor: 'end',
          align: 'end',
          formatter: (value, ctx) => {
            return value + ' %';
          }
        }
      },
      scales: {
        xAxes: [{
          ticks: {
            max: 100
          }
        }]
      },
      tooltips: {
        callbacks: {
          // Create the tooltip label from the dataset value
          label: (tooltipItem) => tooltipItem.value + '%',
        }
      },
      layout: {
        padding: {
            left: 20,
            right: 0,
            top: 0,
            bottom: 0
        }
      }
    },
    plugins: [pluginDataLabels],
    legend: false,
    colors: [
      {
        backgroundColor: [
          'rgb(152,13,36)',
          'rgb(178,41,27)',
          'rgb(204,70,18)',
          'rgb(229,98,9)',
          'rgb(255,126,0)',
          'rgb(223,119,19)',
          'rgb(190,113,37)',
          'rgb(158,106,56)',
          'rgb(125,100,74)',
          'rgb(93,93,93)',
        ]
      }
    ],
  };
  public pieChartConfig: { [key: string]: any } = {
    chartType: 'pie',
    options: {
      responsive: true,
      legend: {
        // Deactivate "hiding" pie slices on legend click
        onClick: () => {},
      },
      tooltips: {
        callbacks: {
          // Get the title from the "first" dataset tooltip item (only one dataset so index 0)
          title: (tooltipItem, data) => data.labels[tooltipItem[0].index],
          // Create the tooltip label from the dataset value
          label: (tooltipItem, data) => data.datasets[0].data[tooltipItem.index] + '%',
        }
      },
      plugins: {
        datalabels: {
          color: '#FFFFFF',
          formatter: (value) => value + '%',
        },
      },
    },
    colors: [
      {
        backgroundColor: [
          'rgb(152, 13, 36)',
          'rgb(255, 126, 0)'
        ]
      }
    ],
  };

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private sondageService: SondageService,
    private sondageReponseService: SondageReponseService,
    private oauthService: OauthService,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private imagesService: ImagesService,
    public userStorageService: UserStorageService,
    private loadingService: LoadingService,
    private toast: ToastDirective,
  ) {
    Chart.defaults.global.defaultFontFamily = 'LatoWeb';
  }

  ngOnDestroy() {
    this.isLoading.unsubscribe();
    this.searchQuery$.unsubscribe();
  }

  ngOnInit(): void {
    this.initializeSondages();
    this.isElu = this.userStorageService.get().roles.indexOf('ROLE_ELU') !== -1;
  }

  initializeSondages(): void {
    this.offset = 0;
    this.sondageService.getAll(this.searchQuery$).subscribe(response => {
      const data = response.data.data;
      const action = response.action;

      if (action === 'scroll' && data.length > 0) {
        data.forEach((sondage: Sondage) => {
          this.sondages.push(sondage);
          this.addFormGroup(sondage);
        });
        this.totalPages = data.length < this.limit;
        this.offset += this.limit;
        this.isLoading.next();

      } else if (action === 'scroll' && data.length === 0) {
        this.totalPages = true;
        this.isLoading.next();

      } else {
        this.sondages = data;
        this.sondageFormGroups = [];
        this.sondages.forEach((sondage: Sondage) => this.addFormGroup(sondage));
        this.offset = this.limit;

        this.totalPages = data.length < this.limit;
        this.noResult = data.length === 0;
      }

      this.loadingService.end();
      this.loaded.emit(true);
    });
  }

  createSearchQuery(action: string): Search {
    const params = {
      clos: 0,
      expired: 0,
      action: action,
      limit: this.limit,
      offset: this.offset,
      order: 'dateDebut',
      orderBy: 'desc'
    };
    return new Search(params);
  }

  private addFormGroup(sondage: Sondage): void {
    const formGroup: FormGroup = this.formBuilder.group({});

    if (sondage.type === SondageType.TypeEnum.Choix) {
      sondage.valeurs.sort((a: SondageValeur, b: SondageValeur) => {
        return a.ordre > b.ordre ? 1 : (a.ordre < b.ordre ? -1 : 0);
      });
      const fieldValue = sondage.vote ? sondage.vote.valeur.id : null;
      formGroup.addControl(
        'valeur',
        new FormControl(fieldValue, [Validators.required])
      );

    } else if (sondage.type === SondageType.TypeEnum.Note) {
      const fieldValue = sondage.vote ? sondage.vote.note : null;
      formGroup.addControl(
        'note',
        new FormControl(
          fieldValue,
          [
            Validators.required,
            Validators.min(1),
            Validators.max(10),
            Validators.pattern('[0-9]{1,2}'),
          ],
        )
      );

    } else if (sondage.type === SondageType.TypeEnum.Affirmatif) {
      const fieldValue = sondage.vote ? (sondage.vote.affirmatif ? 1 : 0) : null;
      formGroup.addControl(
        'affirmatif',
        new FormControl(fieldValue, [Validators.required])
      );
    }
    if (sondage.vote) {
      formGroup.disable();
    }
    this.sondageFormGroups.push(formGroup);

    // Automatically display results for all voted surveys (if allowed)
    if (sondage.vote && (sondage.vueResultat || this.isElu)) {
      this.showResult[this.sondageFormGroups.findIndex((fg: FormGroup) => fg === formGroup)] = true;
    }
  }

  setSondValue(formGroupIndex: number, id: string) {
    this.sondageFormGroups[formGroupIndex].patchValue({
      valeur: id
    });
  }

  vote(sondageId: number, index: number): void {
    this.isVoting = true;
    const reponse: SondageReponse = {
      ...this.getFormGroup(index).value,
      sondage: sondageId
    };
    this.sondageReponseService.vote(reponse).subscribe(
      (response: any) => this.handleSuccess(response),
      (error: any) => this.handleError(error)
    );
  }

  handleSuccess(response: any): void {
    const siteResponse: ResponseMessage = response.json();
    if (siteResponse.message) {
      this.toast.create(siteResponse.message, 3000, 'success');
    }
    this.noResult = true;
    this.offset = 0;
    this.isVoting = false;

    this.voted.emit(true);
  }

  handleError(error: any) {
    const response = this.messageService.handleError(error);
    this.formErrors = response.formErrors;
    this.isVoting = false;
  }

  public getFormGroup(index: number): FormGroup {
    return this.sondageFormGroups[index];
  }

  getImage(id: number, images: Array<any>, type: string, typeImage: string) {
    const image = this.imagesService.getImage(id, images, type, typeImage);

    if (image) {
      return image;
    }

    return 'assets/img/default.jpg';
  }

  public viewResults(sondage: Sondage, index: number): void {
    this.showResult[index] = true;
  }

  public hideResults(index: number): void {
    this.showResult[index] = false;
  }

  doInfinite(infiniteScroll) {
    this.searchQuery$.next(this.createSearchQuery('scroll') as Search);
    this.isLoading.first().subscribe(() => infiniteScroll.complete());
  }
}



// WEBPACK FOOTER //
// ./src/core/sondage-list/sondage-list.component.ts